/**
 * DefaultsSettings.tsx — T321 SETTINGS-1 / FR-004 rewrite.
 *
 * Wires the channel-default fields to
 *   - api.organizations.getChannelDefaults     (read; honest defaults)
 *   - api.organizations.updateChannelDefaults  (admin-only mutation;
 *                                               read-merge-write into
 *                                               systemConfig:channel-defaults)
 *
 * Honest-state contract: every Save is routed through useMutationWithToast
 * (FR-013 (H1), FR-004 (S3)). Logs argument KEYS only (never values).
 *
 * H4 ratchet: this file is no longer the thin re-export shell — the
 * standalone body satisfies the per-sub-page S2 test in
 * frontend-v3/tests/destub/honest-state.test.ts (FR-004).
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import {
  Btn,
  Field,
  Input,
  Panel,
  Select,
  Toggle,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { api } from '@convex/api';

interface ChannelDefaultsDoc {
  edgeProvider: string | null;
  outputDestination: string | null;
  holdingCardTemplate: string | null;
}

export function DefaultsSettings() {
  const current = useQuery(api.organizations.getChannelDefaults) as
    | ChannelDefaultsDoc
    | undefined;

  const updateChannelDefaultsM = useMutationWithToast(
    api.organizations.updateChannelDefaults,
    {
      logLabel: 'organizations_updateChannelDefaults',
      successMessage: 'Valores por defecto actualizados.',
      errorMessagePrefix: 'No se pudieron actualizar los valores por defecto',
    },
  );

  const [edgeProvider, setEdgeProvider] = useState('1080p');
  const [outputDestination, setOutputDestination] = useState('30');
  const [holdingCardTemplate, setHoldingCardTemplate] = useState('default');

  useEffect(() => {
    if (!current) return;
    if (typeof current.edgeProvider === 'string') {
      setEdgeProvider(current.edgeProvider);
    }
    if (typeof current.outputDestination === 'string') {
      setOutputDestination(current.outputDestination);
    }
    if (typeof current.holdingCardTemplate === 'string') {
      setHoldingCardTemplate(current.holdingCardTemplate);
    }
  }, [current]);

  const handleSave = () => {
    void updateChannelDefaultsM.run({
      edgeProvider,
      outputDestination,
      holdingCardTemplate,
    });
  };

  const isSaving = updateChannelDefaultsM.loading;
  const isLoading = current === undefined;

  return (
    <Panel title="Canales por defecto" pad={28}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)' }}>
          Estos valores aplican a canales nuevos. Los existentes conservan su
          configuración.
        </p>
        <Field label="Calidad por defecto">
          <Select
            value={edgeProvider}
            onChange={setEdgeProvider}
            full
            options={['720p', '1080p', '4K']}
          />
        </Field>
        <Field label="Duración de corte por defecto (s)">
          <Input
            value={outputDestination}
            onChange={setOutputDestination}
            disabled={isLoading || isSaving}
          />
        </Field>
        <div style={{ display: 'flex', gap: 24 }}>
          <Field label="Pre-roll">
            <Toggle
              on={holdingCardTemplate.includes('pre')}
              onChange={() =>
                setHoldingCardTemplate(
                  holdingCardTemplate.includes('pre')
                    ? holdingCardTemplate.replace('pre', '')
                    : holdingCardTemplate + 'pre',
                )
              }
            />
          </Field>
          <Field label="Post-roll">
            <Toggle
              on={holdingCardTemplate.includes('post')}
              onChange={() =>
                setHoldingCardTemplate(
                  holdingCardTemplate.includes('post')
                    ? holdingCardTemplate.replace('post', '')
                    : holdingCardTemplate + 'post',
                )
              }
            />
          </Field>
          <Field label="Miniaturas automáticas">
            <Toggle
              on={holdingCardTemplate.includes('thumb')}
              onChange={() =>
                setHoldingCardTemplate(
                  holdingCardTemplate.includes('thumb')
                    ? holdingCardTemplate.replace('thumb', '')
                    : holdingCardTemplate + 'thumb',
                )
              }
            />
          </Field>
        </div>
        <div>
          <Btn
            variant="solid"
            onClick={handleSave}
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Guardando…' : 'Guardar'}
          </Btn>
        </div>
      </div>
    </Panel>
  );
}

export default DefaultsSettings;
